import { ChangeDetectionStrategy, Component, computed, HostListener, input, output } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { chartCustomColors } from '@wheere-front/shared-util';

/** Couleur d'une antenne (0 pour A1) : même palette que les points de la carte. */
export function antennaColour(index: number): string {
  return chartCustomColors[index % chartCustomColors.length];
}

/** Avertissement non bloquant (badge jaune), ex. « 2 ignorés », « non triée ». */
@Component({
  selector: 'lib-warn-badge',
  standalone: true,
  imports: [TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'inline-flex' },
  template: `
    <span
      class="bg-warmYellow-100 inline-flex items-center gap-1 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-bold text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-300"
      [title]="title() || ('siteConfiguration.frequency.nonBlocking' | transloco)">
      <i class="pi pi-exclamation-triangle text-[9px]"></i><ng-content />
    </span>
  `,
})
export class WarnBadgeComponent {
  /** Infobulle : le message serveur, déjà chiffré, quand il y en a un. */
  readonly title = input<string>('');
}

/** Pastille d'antenne « A3 » à la couleur de la carte (grisée quand l'antenne n'existe pas). */
@Component({
  selector: 'lib-antenna-tag',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'inline-flex' },
  template: `
    <span
      class="inline-flex items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-bold"
      [class]="
        dim()
          ? 'bg-gray-50 text-gray-400 dark:bg-slate-800 dark:text-slate-500'
          : 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-200'
      ">
      <span class="inline-block size-2 shrink-0 rounded-[3px]" [style.background]="dim() ? '#d1d5db' : colour()"></span
      >A{{ index() + 1 }}
    </span>
  `,
})
export class AntennaTagComponent {
  /** 0 pour A1. */
  readonly index = input.required<number>();
  readonly dim = input(false);
  protected readonly colour = computed(() => antennaColour(this.index()));
}

/** Courbe du theta (0..3600) sur l'index des fréquences. */
@Component({
  selector: 'lib-theta-spark',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'inline-flex' },
  template: `
    <svg
      [attr.width]="fill() ? '100%' : width()"
      [attr.height]="height()"
      [attr.viewBox]="'0 0 ' + width() + ' ' + height()"
      [attr.preserveAspectRatio]="fill() ? 'none' : null"
      class="text-eletricPurple-500 dark:text-eletricPurple-300 shrink-0"
      role="img"
      aria-label="theta">
      <polyline
        [attr.points]="points()"
        fill="none"
        stroke="currentColor"
        stroke-width="1.25"
        stroke-linejoin="round" />
    </svg>
  `,
})
export class ThetaSparkComponent {
  readonly theta = input.required<readonly number[]>();
  readonly width = input(64);
  readonly height = input(14);
  /** Pleine largeur (aperçu du fichier). */
  readonly fill = input(false);
  protected readonly points = computed(() => {
    const t = this.theta();
    const w = this.width();
    const h = this.height();
    const n = t.length;
    return t
      .map((v, i) => `${((i / Math.max(1, n - 1)) * w).toFixed(1)},${(h - 1 - (v / 3600) * (h - 2)).toFixed(1)}`)
      .join(' ');
  });
}

/** Coquille de modale (même gabarit que les autres modales d'inside), Échap pour fermer. */
@Component({
  selector: 'lib-frequency-modal',
  standalone: true,
  imports: [TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="fixed inset-0 z-[9999] flex items-center justify-center p-4" role="dialog" [attr.aria-label]="title()">
      <div class="absolute inset-0 bg-black/40 dark:bg-black/60" (click)="closed.emit()"></div>
      <div
        class="relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-gray-900 shadow-2xl dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
        [class]="widthClass()">
        <div class="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-3.5 dark:border-slate-700">
          <h3 class="truncate text-base font-semibold text-gray-900 dark:text-slate-100">{{ title() }}</h3>
          <button
            type="button"
            class="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
            [attr.aria-label]="'siteConfiguration.files.close' | transloco"
            (click)="closed.emit()">
            <i class="pi pi-times text-sm"></i>
          </button>
        </div>
        <div class="flex flex-col gap-3 overflow-y-auto px-5 py-4"><ng-content /></div>
        <div class="flex justify-end gap-2 border-t border-gray-100 px-5 py-3.5 dark:border-slate-700">
          <ng-content select="[slot=footer]" />
        </div>
      </div>
    </div>
  `,
})
export class FrequencyModalComponent {
  readonly title = input.required<string>();
  readonly widthClass = input('max-w-[900px]');
  readonly closed = output<void>();

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.closed.emit();
  }
}

/** Petite valeur titrée (résumés de fichier). */
@Component({
  selector: 'lib-kv',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block min-w-0' },
  template: `
    <div class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500">
      {{ label() }}
    </div>
    <div class="truncate text-xs font-semibold tabular-nums text-gray-800 dark:text-slate-100">{{ value() }}</div>
  `,
})
export class KvComponent {
  readonly label = input.required<string>();
  readonly value = input.required<string | number>();
}

/** Téléchargement d'un texte en fichier. */
export function downloadText(fileName: string, content: string | Blob, type = 'application/json'): void {
  const blob = content instanceof Blob ? content : new Blob([content], { type });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(a.href);
}
